import React, { useState, useEffect } from 'react';
import { parseServerTime } from '@/lib/serverTime';
import { useParams, useNavigate } from 'react-router-dom';
import { useMenuIcon } from '@/hooks/useMenuIcon';
import { PageContainer, PageHeader } from '@/components/layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, ChevronLeft, Paperclip, MessageSquare, Trash2, PenSquare, Save, X } from 'lucide-react';
import { inquiryApi, InquiryDetail, InquiryStatus } from '@/api/inquiryApi';
import { LoadingBlock, Spinner } from '@/components/ui/spinner';

const STATUS_LABEL: Record<InquiryStatus, string> = {
  PENDING: '답변대기',
  ANSWERED: '답변완료',
  CLOSED: '완료',
};

const STATUS_CLASS: Record<InquiryStatus, string> = {
  PENDING: 'bg-yellow-100 text-yellow-700 border-yellow-300',
  ANSWERED: 'bg-green-100 text-green-700 border-green-300',
  CLOSED: 'bg-gray-100 text-gray-500 border-gray-300',
};

const MAX_TITLE = 200;
const MAX_CONTENT = 2000;
const MIN_CONTENT = 10;

export default function InquiryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const menuIcon = useMenuIcon();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inquiry, setInquiry] = useState<InquiryDetail | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [editMode, setEditMode] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    inquiryApi.getDetail(Number(id))
      .then(res => setInquiry(res.data))
      .catch(() => setError('문의 내용을 불러오지 못했습니다.'))
      .finally(() => setLoading(false));
  }, [id]);

  const startEdit = () => {
    if (!inquiry) return;
    setEditTitle(inquiry.title);
    setEditContent(inquiry.content);
    setError(null);
    setEditMode(true);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setError(null);
  };

  const handleSave = async () => {
    if (!inquiry) return;
    if (!editTitle.trim()) { setError('제목을 입력해주세요.'); return; }
    if (editContent.trim().length < MIN_CONTENT) { setError(`내용은 ${MIN_CONTENT}자 이상 입력해주세요.`); return; }
    setSaving(true);
    setError(null);
    try {
      await inquiryApi.update(inquiry.id, { title: editTitle.trim(), content: editContent.trim() });
      setInquiry(prev => prev ? { ...prev, title: editTitle.trim(), content: editContent.trim() } : prev);
      setEditMode(false);
    } catch (err: any) {
      if (err.response?.status === 400) {
        setError('답변이 완료된 문의는 수정할 수 없습니다.');
      } else {
        setError('수정 처리 중 오류가 발생했습니다. 다시 시도해주세요.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!inquiry) return;
    if (!window.confirm('문의를 삭제하시겠습니까?')) return;
    setDeleting(true);
    try {
      await inquiryApi.delete(inquiry.id);
      navigate('/dashboard/inquiry');
    } catch (err: any) {
      if (err.response?.status === 400) {
        setError('답변이 완료된 문의는 삭제할 수 없습니다.');
      } else {
        setError('삭제 처리 중 오류가 발생했습니다. 다시 시도해주세요.');
      }
      setDeleting(false);
    }
  };

  const formatDateTime = (iso: string) =>
    parseServerTime(iso)?.toLocaleString('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit'
    }) ?? '-';

  if (loading) {
    return (
      <LoadingBlock size="2xl" py={40} />
    );
  }

  if (error && !inquiry) {
    return (
      <div className="p-4 md:p-8 max-w-3xl mx-auto">
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!inquiry) return null;

  const canEdit = inquiry.status === 'PENDING';

  return (
    <PageContainer maxWidth="full">
      <PageHeader 
        title="1:1 문의"
        icon={menuIcon}
      />

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader className="border-b pb-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <Badge className={STATUS_CLASS[inquiry.status]}>
              {STATUS_LABEL[inquiry.status]}
            </Badge>
            {canEdit && !editMode && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={startEdit}>
                  <PenSquare className="w-3.5 h-3.5 mr-1" />
                  수정
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDelete}
                  disabled={deleting}
                  className="text-red-500 hover:text-red-600 border-red-200 hover:border-red-300"
                >
                  {deleting ? <Spinner size="3.5" className="mr-1" /> : <Trash2 className="w-3.5 h-3.5 mr-1" />}
                  삭제
                </Button>
              </div>
            )}
          </div>

          {editMode ? (
            <div className="mt-3 space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="edit-title" className="text-sm">제목</Label>
                <span className="text-xs text-gray-400">{editTitle.length}/{MAX_TITLE}</span>
              </div>
              <Input
                id="edit-title"
                value={editTitle}
                onChange={e => setEditTitle(e.target.value)}
                maxLength={MAX_TITLE}
                disabled={saving}
              />
            </div>
          ) : (
            <>
              <CardTitle className="text-lg leading-relaxed mt-2">
                {inquiry.isSecret && <span className="text-gray-400 mr-2" title="비밀글">🔒</span>}
                {inquiry.title}
              </CardTitle>
              <div className="text-sm text-gray-400 mt-1">{formatDateTime(inquiry.createdAt)}</div>
            </>
          )}
        </CardHeader>
        <CardContent className="pt-5">
          {editMode ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="edit-content" className="text-sm">내용</Label>
                <span className={`text-xs ${editContent.length > MAX_CONTENT ? 'text-red-500' : 'text-gray-400'}`}>
                  {editContent.length}/{MAX_CONTENT}
                </span>
              </div>
              <textarea
                id="edit-content"
                value={editContent}
                onChange={e => setEditContent(e.target.value)}
                maxLength={MAX_CONTENT}
                rows={10}
                disabled={saving}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
              />
              {inquiry.attachmentUrl && (
                <p className="text-xs text-gray-400"> 첨부파일은 수정할 수 없습니다.</p>
              )}
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" size="sm" onClick={cancelEdit} disabled={saving}>
                  <X className="w-3.5 h-3.5 mr-1" />취소
                </Button>
                <Button size="sm" onClick={handleSave} disabled={saving}>
                  {saving ? <Spinner size="3.5" className="mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                  저장
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="whitespace-pre-wrap text-gray-800 leading-relaxed text-sm" style={{ minHeight: '120px' }}>
                {inquiry.content}
              </div>
              {inquiry.attachmentUrl && (
                <div className="mt-5 pt-4 border-t">
                  <p className="text-xs text-gray-400 mb-2 font-medium">첨부파일</p>
                  <a
                    href={inquiry.attachmentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                  >
                    <Paperclip className="w-3.5 h-3.5" />
                    {inquiry.attachmentName || '첨부파일 다운로드'}
                  </a>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {inquiry.answer ? (
        <Card className="border-blue-200 bg-blue-50/30">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-blue-600" />
              <CardTitle className="text-base text-blue-700">관리자 답변</CardTitle>
              <span className="text-xs text-gray-400 ml-auto">{formatDateTime(inquiry.answer.createdAt)}</span>
            </div>
          </CardHeader>
          <CardContent>
            <div className="whitespace-pre-wrap text-gray-800 leading-relaxed text-sm">
              {inquiry.answer.content}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-dashed border-gray-200">
          <CardContent className="py-8 text-center text-gray-400 text-sm">
            <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-30" />
            아직 답변이 등록되지 않았습니다.
          </CardContent>
        </Card>
      )}

      <div>
        <Button variant="outline" onClick={() => navigate('/dashboard/inquiry')}>
          <ChevronLeft className="w-4 h-4 mr-1" />
          목록으로
        </Button>
      </div>
    </PageContainer>
  );
}